import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppRole } from "@/contexts/AuthContext";
import { ALWAYS_ON_BY_MODULE } from "@/lib/moduleAccessOverrides";

/**
 * Roles allowed by the hard-coded fallback rules for a module.
 * Mirrors FALLBACK_VISIBILITY in useModuleVisibility.ts but exposed
 * for preflight reporting (so we can show the user *why*).
 */
const FALLBACK_VISIBILITY: Record<string, AppRole[]> = {
  attendance: [
    "superadmin", "principal", "deputy_principal",
    "head_administration", "head_academic", "it_incharge", "security_man",
    "hod_cmt", "hod_pst", "hod_sw",
    "cr_cmt4", "cr_cmt5", "cr_cmt6",
    "cr_pst4", "cr_pst5", "cr_pst6",
    "cr_sw4", "cr_sw5", "cr_sw6",
  ],
};

export interface ModuleAccessPreflight {
  loading: boolean;
  granted: boolean;
  /** Roles permitted by DB module_visibility rows (visible=true). */
  dbAllowedRoles: AppRole[];
  /** Roles permitted by the hard-coded fallback rules. */
  fallbackAllowedRoles: AppRole[];
  /** Union of DB + fallback used for the final access decision. */
  effectiveAllowedRoles: AppRole[];
  /** Roles the current user actually holds that grant access. */
  matchedRoles: AppRole[];
  /** True when the DB had at least one visibility row for this module. */
  hasDbRules: boolean;
  /** Source that ultimately granted access, if any. */
  source: "db" | "fallback" | null;
}

/**
 * Validates whether the supplied roles can access the given module
 * BEFORE the route renders, using both the live module_visibility
 * configuration AND the hard-coded fallback rules.
 */
export function useModuleAccessPreflight(
  moduleKey: string,
  roles: AppRole[]
): ModuleAccessPreflight {
  const { data, isLoading } = useQuery({
    queryKey: ["module-access-preflight", moduleKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("module_visibility")
        .select("role, visible")
        .eq("module_key", moduleKey);
      if (error) throw error;
      return (data ?? []) as { role: AppRole; visible: boolean }[];
    },
    staleTime: 60 * 1000,
  });

  const fallbackAllowedRoles = FALLBACK_VISIBILITY[moduleKey] ?? [];
  const alwaysOnRoles = (ALWAYS_ON_BY_MODULE[moduleKey] ?? []) as AppRole[];
  const dbRows = data ?? [];
  const hasDbRules = dbRows.length > 0;
  const dbAllowedRoles = dbRows.filter((r) => r.visible).map((r) => r.role);

  // Effective rule: a role is allowed if DB explicitly allows it OR (no DB row
  // exists for that role) the fallback list permits it. This matches the
  // logic in useModuleVisibility.isModuleVisible.
  const dbDeniedRoles = new Set(
    dbRows.filter((r) => !r.visible).map((r) => r.role)
  );
  const effectiveSet = new Set<AppRole>(dbAllowedRoles);
  for (const r of fallbackAllowedRoles) {
    if (!dbDeniedRoles.has(r) && !dbRows.some((row) => row.role === r)) {
      effectiveSet.add(r);
    }
  }
  // Always-on roles are unconditionally part of the effective set.
  for (const r of alwaysOnRoles) effectiveSet.add(r);

  // Hard-wired override: if the user holds any always-on role, grant immediately.
  const matchedAlwaysOn = roles.filter((r) => alwaysOnRoles.includes(r));
  if (matchedAlwaysOn.length > 0) {
    return {
      loading: isLoading,
      granted: true,
      dbAllowedRoles,
      fallbackAllowedRoles,
      effectiveAllowedRoles: Array.from(effectiveSet),
      matchedRoles: matchedAlwaysOn,
      hasDbRules,
      source: "fallback",
    };
  }

  const matchedDb = roles.filter((r) => dbAllowedRoles.includes(r));
  const matchedFallback = roles.filter(
    (r) => fallbackAllowedRoles.includes(r) && !dbDeniedRoles.has(r) && !dbRows.some((row) => row.role === r)
  );
  const matchedRoles = Array.from(new Set([...matchedDb, ...matchedFallback]));

  const granted = matchedRoles.length > 0;
  const source: "db" | "fallback" | null =
    matchedDb.length > 0 ? "db" : matchedFallback.length > 0 ? "fallback" : null;

  return {
    loading: isLoading,
    granted,
    dbAllowedRoles,
    fallbackAllowedRoles,
    effectiveAllowedRoles: Array.from(effectiveSet),
    matchedRoles,
    hasDbRules,
    source,
  };
}
